import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Tooltip } from 'react-tooltip';

import { billingPeriod } from 'utilities/billing_period';
import { formatPrice } from 'utilities/money';

import { GreenButton, PurpleButton, WhiteButton } from '@marketing/components/buttons';
import { CurrentPlanProps, OfferProps, OfferToggleProps, PlanProps } from '@marketing/types';
import { periodSuffix } from '@marketing/utilities/offer_price';

import ArrowWithLoop from './assets/arrow_with_loop.svg'
import CheckmarkIcon from './assets/checkmark_icon.svg';
import styles from './style.module.scss';

// A plan priced below its usual price shows the on-sale badge beside the crossed-out price
const showsSaleBadge = (plan: PlanProps) => plan.priceInCents !== plan.fullPriceInCents;

// Colors of the on-sale badge, which a toggle borrows when the card has none of its own
const saleBadgeBackground = 'linear-gradient(to bottom, rgb(253, 199, 66), rgb(252, 185, 20))';
const saleBadgeText = '#222123';

// A discount that only covers the first payment leaves later payments costing more
const showsRenewalPrice = (plan: PlanProps) => (
  plan.renewalPriceInCents != null && plan.renewalPriceInCents !== plan.priceInCents
);

// When the price only holds for the first payment, price it as the first period rather than as the
// ongoing rate
const priceSuffix = (plan: PlanProps) => (
  showsRenewalPrice(plan) ? ` / first ${billingPeriod(plan)}` : periodSuffix(plan)
);

// A recurring plan's price already reads as a rate ('$30 / month'), so leave the currency off it
const currencySuffix = (plan: PlanProps) => (plan.recurring ? '' : ' USD');

// The student's own plan isn't something to buy again, so its button goes nowhere
const isCurrentPlan = (plan: PlanProps, props: Props) => (
  !!props.currentPlan && plan.slug === props.currentPlan.slug
);

// A student already on a plan they can change moves to a recurring plan rather than buying another,
// so its button says so. Every other button keeps the label its purchase option gives it.
const currentPlanLabel = (plan: PlanProps, props: Props) => {
  if (isCurrentPlan(plan, props)) return 'Your Current Plan';

  return props.currentPlan?.canChangePlan && plan.recurring ? `Change Plan To ${plan.name}` : undefined;
};

// The plan a card prices and sells: the second when its toggle is switched on, the first otherwise
const shownPlan = (offer: OfferProps, toggledOn: boolean) => (
  offer.plans[offer.toggle && toggledOn ? 1 : 0]
);

// A card's copy, alongside the offer it sells. A card whose offer the exam doesn't sell is left out.
interface PaidOffer {
  header: string | React.ReactNode;
  highlight?: string | React.ReactNode;
  offer?: OfferProps;
  subHeader: string;
  valuePropGroups: {
    content: {
      content: string | React.ReactNode;
      icon?: string | React.ReactNode;
      tooltip?: string;
    }[];
    header: string;
    subHeader?: string;
  }[];
}

interface TrialOffer {
  header: string | React.ReactNode;
  offer: OfferProps;
  subHeader: string;
}

type PaidOfferPlace = 'primary' | 'secondary' | 'tertiary';

interface Props {
  currentPlan?: CurrentPlanProps | null;
  primaryOffer: PaidOffer;
  secondaryOffer?: PaidOffer;
  tertiaryOffer?: PaidOffer;
  trialOffer: TrialOffer;
}

export default (props: Props) => {
  // Every card's toggle switches together
  const [toggledOn, setToggledOn] = useState(false);

  const primaryPlan = shownPlan(props.primaryOffer.offer, toggledOn);
  const secondaryPlan = props.secondaryOffer?.offer && shownPlan(props.secondaryOffer.offer, toggledOn);
  const tertiaryPlan = props.tertiaryOffer?.offer && shownPlan(props.tertiaryOffer.offer, toggledOn);
  const trialPlan = props.trialOffer.offer.plans[0];

  const toggleSwitch = (place: PaidOfferPlace, offer: OfferProps, plan: PlanProps) => (
    offer.toggle && (
      <PlanToggle
        cardOnSale={showsSaleBadge(plan)}
        on={toggledOn}
        onChange={setToggledOn}
        place={place}
        toggle={offer.toggle}
      />
    )
  );

  const prepareSubtextLine = (subtextLine: string, plan: PlanProps) => {
    let line = subtextLine;

    line = line.replace(
      '{{original-price}}',
      formatPrice(plan.fullPriceInCents, 'usd', true)
    );

    line = line.replace(
      '{{new-price}}',
      formatPrice(plan.priceInCents, 'usd', true)
    );

    return line;
  };

  const Feature = (props) => (
    <div
      className="d-flex gap-xs"
      data-tooltip-id="feature-tooltip"
      data-tooltip-html={props.tooltip}
    >
      {
        props.icon || (
          <img
            alt="Checkmark icon"
            height="16px"
            loading="lazy"
            src={CheckmarkIcon}
            style={{ height: 16, marginTop: 2, width: 14 }}
            width="14px"
          />
        )
      }

      <div className={cx(props.className, 'small text-inter')}>
        <span className={cx({ 'text-underdash': !!props.tooltip })}>
          {props.children}
        </span>
      </div>
    </div>
  );

  const OnSaleBadge = () => (
    <div
      className="align-items-center d-flex gap-xs"
      style={{
        background: saleBadgeBackground,
        borderRadius: 4,
        fontSize: 11,
        padding: '4px 8px'
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none">
        <path fill="#C38E0B" d="M12.24 6.278 6.722.76A.881.881 0 0 0 6.094.5H.944A.444.444 0 0 0 .5.944v5.15a.881.881 0 0 0 .26.628l5.518 5.518a.89.89 0 0 0 1.257 0l4.705-4.705a.89.89 0 0 0 0-1.257ZM3.389 4.056a.667.667 0 1 1 0-1.334.667.667 0 0 1 0 1.334Z" />
      </svg>

      <strong>ON SALE</strong>
    </div>
  );

  return (
    <div className={styles.offersContainer}>
      {/* Offsetting the target to make room for the sticky navbar and notice bar */}
      <div id="offers" style={{ marginTop: -140, position: 'absolute' }} />

      {
        props.currentPlan?.canChangePlan && (
          <div
            className="text-center text-inter u-margin-B-m"
            style={{
              background: '#F3EEF9',
              border: '1px solid #D9CCEB',
              borderRadius: 8,
              color: '#472375',
              padding: '12px 16px'
            }}
          >
            You&apos;re currently on the <strong>{props.currentPlan.name}</strong> plan. Select a
            different plan below.
          </div>
        )
      }

      {
        props.primaryOffer.highlight && (
          <div className="hidden-xs hidden-sm text-inter text-right u-padding-B-m u-padding-R-m" style={{ color: '#472375' }}>
            {props.primaryOffer.highlight}
          </div>
        )
      }

      <div
        className={cx(styles.pricingAndFeaturesContainer, 'd-flex flex-column gap-m')}
        style={{
          background: '#F7F7F7',
          border: '1px solid #DEDDDB',
          boxShadow: 'inset 0 2px 3px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className={cx(styles.offersGrid, 'd-flex flex-column flex-lg-row gap-l gap-lg-m text-inter')}>
          {
            tertiaryPlan && (
              <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
                <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#FFFFFF' }}>
                  <div
                    className="d-flex flex-column gap-xs"
                    style={{ background: '#F7F7F7', borderRadius: 16, padding: 10 }}
                  >
                    <div style={{ fontSize: 18 }}>
                      {props.tertiaryOffer.header}
                    </div>

                    <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                      {props.tertiaryOffer.subHeader}
                    </div>
                  </div>

                  <ExpandableContentAndFeatures
                    className="d-flex flex-column flex-1 gap-m"
                    triggerColor="#222123"
                  >
                    {
                      props.tertiaryOffer.valuePropGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className={styles.offerContentAndFeatures}>
                          <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                            {group.header}
                          </div>

                          {
                            group.content.map((valueProp, idx) => (
                              <Feature key={idx} icon={valueProp.icon} tooltip={valueProp.tooltip}>
                                {valueProp.content}
                              </Feature>
                            ))
                          }
                        </div>
                      ))
                    }
                  </ExpandableContentAndFeatures>

                  <div className="d-flex flex-column gap-m justify-content-end">
                    <div className="d-flex flex-column flex-1 gap-xs">
                      {
                        showsSaleBadge(tertiaryPlan) && (
                          <div className="d-flex gap-s">
                            <OnSaleBadge />

                            <strong
                              className="text-medium text-gray-darker"
                              style={{ fontSize: 15, textDecoration: 'line-through' }}
                            >
                              Usually {formatPrice(tertiaryPlan.fullPriceInCents, 'usd', true)}{periodSuffix(tertiaryPlan)}
                            </strong>
                          </div>
                        )
                      }

                      <div className="text-gray-darker" style={{ fontSize: 30 }}>
                        <strong>{formatPrice(tertiaryPlan.priceInCents, 'usd', true)}{currencySuffix(tertiaryPlan)}{priceSuffix(tertiaryPlan)}</strong>
                      </div>

                      {toggleSwitch('tertiary', props.tertiaryOffer.offer, tertiaryPlan)}

                      {
                        showsRenewalPrice(tertiaryPlan) && (
                          <div style={{ color: '#5C5C5C', fontSize: 14 }}>
                            Then <strong>{formatPrice(tertiaryPlan.renewalPriceInCents, 'usd', true)}</strong>
                            {periodSuffix(tertiaryPlan)}
                          </div>
                        )
                      }

                      {
                        tertiaryPlan.subtextLines.length !== 0 && (
                          <div
                            className="d-flex flex-column"
                            style={{
                              borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                              marginTop: 10,
                              padding: '10px 0',
                              width: '100%'
                            }}
                          >
                            {
                              tertiaryPlan.subtextLines.map((subtextLine, idx) => (
                                <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                                  <strong>{prepareSubtextLine(subtextLine, tertiaryPlan)}</strong>
                                </div>
                              ))
                            }
                          </div>
                        )
                      }

                      {
                        tertiaryPlan.installmentsAvailable ? (
                          <div style={{ color: '#5C5C5C', fontSize: 14, height: 20 }}>
                            Or 4 monthly payments of {formatPrice(Math.round(tertiaryPlan.priceInCents / 4))}
                          </div>
                        ) : (
                          /* Placeholder to match height of monthly installments div on other plans */
                          <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                        )
                      }
                    </div>

                    <PurchaseButtons
                      button={WhiteButton}
                      disabled={isCurrentPlan(tertiaryPlan, props)}
                      labelOverride={currentPlanLabel(tertiaryPlan, props)}
                      plan={tertiaryPlan}
                    />
                  </div>
                </div>
              </div>
            )
          }

          {
            secondaryPlan && (
              <div className={cx(styles.offer, 'd-flex flex-column flex-1')}>
                <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#E9F8F2' }}>
                  <div
                    className="d-flex flex-column gap-xs"
                    style={{ background: '#C2EBDB', borderRadius: 16, padding: 10 }}
                  >
                    <div style={{ fontSize: 18 }}>
                      {props.secondaryOffer.header}
                    </div>

                    <div style={{ color: '#5C5C5C', fontSize: 13 }}>
                      {props.secondaryOffer.subHeader}
                    </div>
                  </div>

                  <ExpandableContentAndFeatures
                    className="d-flex flex-column flex-1 gap-m"
                    triggerColor="#222123"
                  >
                    {
                      props.secondaryOffer.valuePropGroups.map((group, groupIdx) => (
                        <div key={groupIdx} className={styles.offerContentAndFeatures}>
                          <div className="text-medium text-upper" style={{ color: '#5C5C5C', fontSize: 12 }}>
                            {group.header}
                          </div>

                          {
                            group.content.map((valueProp, idx) => (
                              <Feature key={idx} icon={valueProp.icon} tooltip={valueProp.tooltip}>
                                {valueProp.content}
                              </Feature>
                            ))
                          }
                        </div>
                      ))
                    }
                  </ExpandableContentAndFeatures>

                  <div className="d-flex flex-column gap-m justify-content-end">
                    <div className="d-flex flex-column flex-1 gap-xs">
                      {
                        showsSaleBadge(secondaryPlan) && (
                          <div className="d-flex gap-s">
                            <OnSaleBadge />

                            <strong
                              className="text-medium text-gray-darker"
                              style={{ fontSize: 15, textDecoration: 'line-through' }}
                            >
                              Usually {formatPrice(secondaryPlan.fullPriceInCents, 'usd', true)}{periodSuffix(secondaryPlan)}
                            </strong>
                          </div>
                        )
                      }

                      <div className="text-gray-darker" style={{ fontSize: 30 }}>
                        <strong>{formatPrice(secondaryPlan.priceInCents, 'usd', true)}{currencySuffix(secondaryPlan)}{priceSuffix(secondaryPlan)}</strong>
                      </div>

                      {toggleSwitch('secondary', props.secondaryOffer.offer, secondaryPlan)}

                      {
                        showsRenewalPrice(secondaryPlan) && (
                          <div style={{ color: '#5C5C5C', fontSize: 14 }}>
                            Then <strong>{formatPrice(secondaryPlan.renewalPriceInCents, 'usd', true)}</strong>
                            {periodSuffix(secondaryPlan)}
                          </div>
                        )
                      }

                      {
                        secondaryPlan.subtextLines.length !== 0 && (
                          <div
                            className="d-flex flex-column"
                            style={{
                              borderTop: '1px solid rgba(0, 0, 0, 0.07)',
                              marginTop: 10,
                              padding: '10px 0',
                              width: '100%'
                            }}
                          >
                            {
                              secondaryPlan.subtextLines.map((subtextLine, idx) => (
                                <div key={idx} style={{ color: '#5C5C5C', fontSize: 14 }}>
                                  <strong>{prepareSubtextLine(subtextLine, secondaryPlan)}</strong>
                                </div>
                              ))
                            }
                          </div>
                        )
                      }

                      {
                        secondaryPlan.installmentsAvailable ? (
                          <div style={{ color: '#5C5C5C', fontSize: 14, height: 20 }}>
                            Or 4 monthly payments of {formatPrice(Math.round(secondaryPlan.priceInCents / 4))}
                          </div>
                        ) : (
                          /* Placeholder to match height of monthly installments div on other plans */
                          <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                        )
                      }
                    </div>

                    <PurchaseButtons
                      button={GreenButton}
                      className="text-gray-darker"
                      disabled={isCurrentPlan(secondaryPlan, props)}
                      labelOverride={currentPlanLabel(secondaryPlan, props)}
                      plan={secondaryPlan}
                    />
                  </div>
                </div>
              </div>
            )
          }

          <div className="d-flex flex-column flex-1">
            {
              props.primaryOffer.highlight && (
                 <div className="text-inter text-center u-padding-B-s visible-xs visible-sm" style={{ color: '#472375' }}>
                  {props.primaryOffer.highlight}
                </div>
              )
            }

            <div className={cx(styles.offer, 'd-flex flex-column flex-1')} style={{ position: 'relative' }}>
              {
                props.primaryOffer.highlight && (
                  <div className={styles.arrowWithLoop}>
                    <img alt="" src={ArrowWithLoop} />
                  </div>
                )
              }

              <div className="d-flex flex-1 flex-column gap-m" style={{ background: '#472375' }}>
                <div
                  className="d-flex flex-column gap-xs"
                  style={{ background: '#5D3391', borderRadius: 16, padding: 10 }}
                >
                  <div style={{ fontSize: 18 }}>
                    {props.primaryOffer.header}
                  </div>

                  <div style={{ color: '#DDB2FF', fontSize: 13 }}>
                    {props.primaryOffer.subHeader}
                  </div>
                </div>

                <ExpandableContentAndFeatures
                  className="d-flex flex-column flex-1 gap-m"
                  triggerColor="#FFFFFF"
                >
                  {
                    props.primaryOffer.valuePropGroups.map((group, groupIdx) => (
                      <div key={groupIdx} className={styles.offerContentAndFeatures}>
                        <div className="text-medium text-upper" style={{ color: '#DDB2FF', fontSize: 12 }}>
                          {group.header}
                        </div>

                        {
                          group.subHeader && (
                            <div style={{ color: '#DDB2FF', fontSize: 14 }}>
                              {group.subHeader}
                            </div>
                          )
                        }

                        {
                          group.content.map((valueProp, idx) => (
                            <Feature key={idx} className="text-white" icon={valueProp.icon} tooltip={valueProp.tooltip}>
                              {valueProp.content}
                            </Feature>
                          ))
                        }
                      </div>
                    ))
                  }
                </ExpandableContentAndFeatures>

                <div className="d-flex flex-column gap-m justify-content-end">
                  <div className="d-flex flex-column flex-1 gap-xs">
                    {
                      showsSaleBadge(primaryPlan) && (
                        <div className="d-flex gap-s">
                          <OnSaleBadge />

                          <strong
                            className="text-medium text-white"
                            style={{ fontSize: 15, textDecoration: 'line-through' }}
                          >
                            Usually {formatPrice(primaryPlan.fullPriceInCents, 'usd', true)}{periodSuffix(primaryPlan)}
                          </strong>
                        </div>
                      )
                    }
                    <div className="d-flex gap-s text-white" style={{ fontSize: 30 }}>
                      <strong>{formatPrice(primaryPlan.priceInCents, 'usd', true)}{currencySuffix(primaryPlan)}{priceSuffix(primaryPlan)}</strong>
                    </div>

                    {toggleSwitch('primary', props.primaryOffer.offer, primaryPlan)}

                    {
                      showsRenewalPrice(primaryPlan) && (
                        <div style={{ color: '#DDB2FF', fontSize: 14 }}>
                          Then <strong className="text-white">{formatPrice(primaryPlan.renewalPriceInCents, 'usd', true)}</strong>
                          {periodSuffix(primaryPlan)}
                        </div>
                      )
                    }

                    {
                      primaryPlan.subtextLines.length !== 0 && (
                        <div
                          className="d-flex flex-column"
                          style={{
                            borderTop: '1px solid #DDB2FF',
                            marginTop: 10,
                            padding: '10px 0',
                            width: '100%'
                          }}
                        >
                          {
                            primaryPlan.subtextLines.map((subtextLine, idx) => (
                              <div key={idx} style={{ color: '#FFFFFF', fontSize: 14 }}>
                                <strong>{prepareSubtextLine(subtextLine, primaryPlan)}</strong>
                              </div>
                            ))
                          }
                        </div>
                      )
                    }

                    {
                      primaryPlan.installmentsAvailable ? (
                        <div style={{ color: '#DDB2FF', fontSize: 14, height: 20 }}>
                          Or 4 monthly payments of {formatPrice(Math.round(primaryPlan.priceInCents / 4))}
                        </div>
                      ) : (
                        /* Placeholder to match height of monthly installments div on other plans */
                        <div className="hidden-xs hidden-sm" style={{ height: 20 }} />
                      )
                    }
                  </div>

                  <PurchaseButtons
                    button={PurpleButton}
                    disabled={isCurrentPlan(primaryPlan, props)}
                    labelOverride={currentPlanLabel(primaryPlan, props)}
                    plan={primaryPlan}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {
          // A student with a plan to change has no use for a free trial
          !props.currentPlan?.canChangePlan && (
            <div className="align-items-center d-flex flex-column gap-s u-margin-V-m">
              <div className="align-items-center d-flex flex-column text-inter" style={{ color: '#5C5C5C' }}>
                <div className="text-center"><strong>{props.trialOffer.header}</strong></div>
                <div className="text-center">{props.trialOffer.subHeader}</div>
              </div>

              <div>
                <PurchaseButtons
                  button={WhiteButton}
                  disabled={isCurrentPlan(trialPlan, props)}
                  labelOverride={currentPlanLabel(trialPlan, props)}
                  plan={trialPlan}
                  size="sm"
                />
              </div>
            </div>
          )
        }
      </div>

      <Tooltip className="text-center" id="feature-tooltip" />
    </div>
  );
};


// Each card's toggle takes on the background of the card's sign-up button, for its badge and for
// its switch while on. The tertiary card's button is white, so on its white card the badge takes an
// outline and the switch goes from light to dark gray instead.
const toggleColors: Record<PaidOfferPlace, {
  badgeBackground: string;
  badgeText: string;
  // Outlines the badge, drawn inside so it doesn't change its size
  outline?: string;
  message: string;
  trackOff: string;
  trackOn: string;
}> = {
  primary: {
    badgeBackground: '#B674FF',
    badgeText: '#FFFFFF',
    message: '#FFFFFF',
    trackOff: '#DAB8FF',
    trackOn: '#B674FF'
  },
  secondary: {
    badgeBackground: '#26B67C',
    badgeText: '#FFFFFF',
    message: '#5C5C5C',
    trackOff: 'rgb(194, 235, 219)',
    trackOn: '#26B67C'
  },
  tertiary: {
    badgeBackground: '#FFFFFF',
    badgeText: '#000000',
    outline: 'inset 0 0 0 1px #D9D8DA',
    message: '#5C5C5C',
    trackOff: '#DEDDDB',
    trackOn: '#5C5C5C'
  }
};

// Switches a card between its two plans: the switch, then its badge, then its message. The badge
// says the same thing either way, gaining a checkmark while the switch is on. The badge, and the
// switch while on, take on the yellow of the on-sale badge unless the card is already showing that
// badge, when they take the card's own colors instead.
const PlanToggle: React.FC<{
  // Whether the card shows its on-sale badge
  cardOnSale: boolean;
  on: boolean;
  onChange: (on: boolean) => void;
  place: PaidOfferPlace;
  toggle: OfferToggleProps;
}> = ({ cardOnSale, on, onChange, place, toggle }) => {
  // The on-sale badge's yellow, unless the card already shows that badge
  const colors = cardOnSale ? toggleColors[place] : {
    ...toggleColors[place],
    badgeBackground: saleBadgeBackground,
    badgeText: saleBadgeText,
    outline: undefined,
    trackOn: saleBadgeBackground
  };

  return (
    <button
      aria-checked={on}
      className="align-items-center d-flex flex-wrap gap-s"
      onClick={() => onChange(!on)}
      role="switch"
      style={{
        background: 'none',
        border: 0,
        color: colors.message,
        cursor: 'pointer',
        fontSize: 14,
        padding: 0,
        textAlign: 'left'
      }}
      type="button"
    >
      <span
        style={{
          background: on ? colors.trackOn : colors.trackOff,
          borderRadius: 10,
          display: 'inline-block',
          flexShrink: 0,
          height: 20,
          position: 'relative',
          transition: 'background 0.2s',
          width: 36
        }}
      >
        <span
          style={{
            background: '#FFFFFF',
            borderRadius: '50%',
            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.2)',
            height: 16,
            left: on ? 18 : 2,
            position: 'absolute',
            top: 2,
            transition: 'left 0.2s',
            width: 16
          }}
        />
      </span>

      {
        toggle.badge && (
          <span
            className="align-items-center d-flex gap-xs"
            style={{
              background: colors.badgeBackground,
              borderRadius: 4,
              boxShadow: colors.outline,
              color: colors.badgeText,
              fontSize: 11,
              padding: '4px 8px'
            }}
          >
            {
              on && (
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="none" viewBox="0 0 10 10">
                  <path
                    d="M1.5 5.5 4 8l4.5-6"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.6"
                  />
                </svg>
              )
            }

            <strong>{toggle.badge}</strong>
          </span>
        )
      }

      {toggle.message}
    </button>
  );
};

// Renders one button per purchase option, labeled as the option says
const PurchaseButtons: React.FC<{
  button: React.FC<{
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    href?: string;
    size?: null | 'sm';
  }>;
  className?: string;
  disabled?: boolean;
  // Replaces every option's label, for a student already on a plan
  labelOverride?: string;
  plan: PlanProps;
  size?: null | 'sm';
}> = ({ button: Button, className, disabled, labelOverride, plan, size }) => {
  return (
    <div className="d-flex flex-column gap-s">
      {
        plan.purchaseOptions.map((purchaseOption, idx) => (
          <Button
            key={idx}
            className={className}
            disabled={disabled}
            href={purchaseOption.registrationUrl}
            size={size}
          >
            {labelOverride || purchaseOption.label}
          </Button>
        ))
      }
    </div>
  );
};

const ExpandableContentAndFeatures: React.FC<{ children: any; className?: string; triggerColor: string }> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const determineContentHeight = useRef(() => {
    if (ref.current) {
      setContentHeight(ref.current.clientHeight);
    }
  });

  useEffect(() => {
    determineContentHeight.current();

    window.addEventListener('resize', determineContentHeight.current);

    return () => {
      window.removeEventListener('resize', determineContentHeight.current);
    };
  }, [props.children]);

  return (
    <>
      <div className="visible-xs visible-sm u-margin-B-s">
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <span className="text-inter" style={{ color: props.triggerColor, fontSize: 14 }}>
            <strong>See plan details</strong>
          </span>

          <span style={{ paddingLeft: 5, transform: isExpanded ? 'rotate(180deg)': '' }}>
            <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.75 1L4.0429 4.2929C4.4334 4.6834 5.0666 4.6834 5.4571 4.2929L8.75 1" stroke={props.triggerColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>

        <div
          style={{
            height: isExpanded ? contentHeight : 0,
            overflow: 'hidden',
            transition: 'height 200ms'
          }}
        >
          <div className={props.className} ref={ref}>
            {props.children}
          </div>
        </div>
      </div>

      <div className="flex-1 hidden-xs hidden-sm">
        <div className={props.className}>
          {props.children}
        </div>
      </div>
    </>
  );
};
